/*!

=========================================================
* Black Dashboard React v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/black-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/black-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useEffect, useState, useRef } from "react";

// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  CardText,
  FormGroup,
  Form,
  Input,
  Label,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Badge,
  Progress,
} from "reactstrap";
import { useHistory } from "react-router-dom";
import { toProperCase } from "utils/func";
import { logApp } from "utils/func";
import axios from "axios";
import { API_TIMEOUT } from "utils";
import swal from "sweetalert";
import { API_DONOR_DARAH } from "utils";

function PermintaanDonor() {
  const [currentUser, setCurrentUser] = useState("");
  const history = useHistory();

  const [jenisPermintaan, setJenisPermintaan] = useState(""); // "bank_darah" atau "pasien"
  const [nama, setNama] = useState("");
  const [golda, setGolda] = useState("");
  const [tanggal, setTanggal] = useState("");
  const [alasan, setAlasan] = useState("");
  const [lokasi, setLokasi] = useState("");
  const [jumlah, setJumlah] = useState("");

  const [listLokasi, setListLokasi] = useState([]);
  const [listAlasan, setListAlasan] = useState([]);
  const [listGolda, setListGolda] = useState([]);

  // State untuk WhatsApp
  const [modalWhatsAppOpen, setModalWhatsAppOpen] = useState(false);
  const [listPendonor, setListPendonor] = useState([]);
  const [draftPesan, setDraftPesan] = useState("");
  const [selectedGolda, setSelectedGolda] = useState("");
  const [selectedGoldaName, setSelectedGoldaName] = useState("");
  const [jumlahKantung, setJumlahKantung] = useState("");
  const [namaPenerima, setNamaPenerima] = useState("");
  const [tanggalPermintaan, setTanggalPermintaan] = useState("");
  const [lokasiDonor, setLokasiDonor] = useState("");
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [progressWhatsApp, setProgressWhatsApp] = useState({
    current: 0,
    total: 0,
  });
  const [detailPengiriman, setDetailPengiriman] = useState([]);
  const cancelSendingRef = useRef(false);
  const isManualEditRef = useRef(false);
  const lastGeneratedPesanRef = useRef("");
  const [permintaanID, setPermintaanID] = useState(null);
  const [wablasConfig, setWablasConfig] = useState({
    url: "",
    tokenwhatsapp: "",
    secret_key: "",
  });
  const [canSendMessage, setCanSendMessage] = useState(false); // Flag untuk aktifkan tombol Kirim Pesan
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [allBatches, setAllBatches] = useState([]);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });

  // Fungsi delay untuk menghindari rate limiting
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Fungsi untuk membatalkan pengiriman
  const handleCancelSending = () => {
    cancelSendingRef.current = true;
    setSendingWhatsApp(false);
    swal(
      "Dibatalkan",
      `Pengiriman pesan dibatalkan. ${progressWhatsApp.current} dari ${progressWhatsApp.total} pesan sudah terkirim.`,
      "info"
    );
  };

  useEffect(() => {
    getListAlasan();
    getListLokasi();
    getListGolda();
    getWablasConfig();
    // Jika data yang disimpan di localStorage adalah dalam format JSON, Anda perlu menguraikannya menjadi objek
    if (!currentUser) {
      let user = localStorage.getItem("user");
      if (user) {
        user = JSON.parse(user);
        setCurrentUser(user);
        logApp(user.uid, "Permintaan Darah");
      } else {
        history.push("/login");
      }
    }
  }, []);

  // Fungsi untuk mengambil konfigurasi wablas aktif
  const getWablasConfig = async () => {
    try {
      const response = await axios.get(`${API_DONOR_DARAH}wablas/active`, {
        timeout: API_TIMEOUT,
      });

      if (response.data?.success === 1 && response.data.data) {
        setWablasConfig({
          url: response.data.data.url,
          tokenwhatsapp: response.data.data.tokenwhatsapp,
          secret_key: response.data.data.secret_key || "",
        });
      } else {
        // Jika tidak ada konfigurasi aktif, tetap gunakan default
        console.log("Tidak ada konfigurasi wablas aktif, menggunakan default");
      }
    } catch (error) {
      console.error("Error fetching wablas config:", error);
      // Gunakan default jika error
    }
  };

  // Generate draft pesan secara real-time saat form diisi
  useEffect(() => {
    if (
      jenisPermintaan &&
      golda &&
      jumlah &&
      tanggal &&
      lokasi &&
      listGolda.length > 0 &&
      listLokasi.length > 0
    ) {
      // Jika bank darah, nama otomatis "Bank Darah"
      const namaFinal = jenisPermintaan === "bank_darah" ? "Bank Darah" : nama;

      if (
        jenisPermintaan === "bank_darah" ||
        (jenisPermintaan === "pasien" && nama)
      ) {
        // Ambil info golongan darah
        const selectedBloodType = listGolda.find(
          (bt) => bt.bloodTypeID == golda
        );
        const golonganDarahName = selectedBloodType
          ? selectedBloodType.golonganDarah
          : "Golongan Darah";

        // Ambil info lokasi
        const lokasiIDNum = parseInt(lokasi);
        const selectedLokasi = listLokasi.find(
          (loc) => parseInt(loc.id) === lokasiIDNum
        );
        const lokasiName = selectedLokasi
          ? selectedLokasi.lokasi
          : "Lokasi Donor";

        // Format tanggal
        let tanggalFormatted = "";
        if (tanggal) {
          const tgl = new Date(tanggal);
          tanggalFormatted = tgl.toLocaleDateString("id-ID", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          });
        }

        let pesan = "";

        // Jika Bank Darah, gunakan pesan khusus
        if (jenisPermintaan === "bank_darah") {
          pesan = `🩸 *HIMBAUAN DONOR DARAH* 🩸

Halo *[Nama Pendonor]* Pendonor Darah,

Kami mengundang Bapak/Ibu untuk berpartisipasi dalam kegiatan donor darah untuk mengisi stok darah pada Bank Darah di RSUD Ba'a.

📋 *Detail Permintaan:*
• Golongan Darah: *${golonganDarahName}*
• Jumlah Kantung: *${jumlah} kantong*
• Untuk: *Bank Darah*
• Tanggal: *${tanggalFormatted || tanggal}*
• Lokasi: *${lokasiName}*
• Tujuan: *Pendonor Sukarela (untuk permintaan bank darah)*

Kami sangat mengharapkan partisipasi Anda untuk membantu mengisi stok darah di Bank Darah RSUD Ba'a.

🔗 *Form Response:*
[LINK_RESPONSE]

Silakan klik link di atas untuk mengisi form response jika Anda bersedia membantu.

Terima kasih atas kebaikan hati Anda.

🙏🙏🙏
*Sistem Donor Darah - Dinkes Kab. Rote Ndao*`;
        } else {
          // Jika Pasien, gunakan pesan seperti biasa
          const tujuanText =
            alasan == 1
              ? "Pendonor Pengganti (untuk permintaan pasien)"
              : alasan == 2
              ? "Pendonor Sukarela (untuk permintaan bank darah)"
              : "[Tujuan akan diisi otomatis]";

          pesan = `🩸 *PERMINTAAN DARAH URGENT* 🩸

Halo *[Nama Pendonor]* Pendonor Darah,

Kami membutuhkan bantuan Anda untuk mendonorkan darah dengan detail sebagai berikut:

📋 *Detail Permintaan:*
• Golongan Darah: *${golonganDarahName}*
• Jumlah Kantung: *${jumlah} kantong*
• Untuk: *${namaFinal}*
• Tanggal: *${tanggalFormatted || tanggal}*
• Lokasi: *${lokasiName}*
• Tujuan: *${tujuanText}*

Kami sangat mengharapkan partisipasi Anda untuk membantu sesama yang membutuhkan.

🔗 *Form Response:*
[LINK_RESPONSE]

Silakan klik link di atas untuk mengisi form response jika Anda bersedia membantu.

Terima kasih atas kebaikan hati Anda.

🙏🙏🙏
*Sistem Donor Darah - Dinkes Kab. Rote Ndao*`;
        }

        // Hanya update jika user belum edit manual atau pesan yang di-generate berbeda
        if (
          !isManualEditRef.current ||
          lastGeneratedPesanRef.current !== pesan
        ) {
          setDraftPesan(pesan);
          lastGeneratedPesanRef.current = pesan;
          // Reset flag manual edit jika pesan baru di-generate
          if (lastGeneratedPesanRef.current === pesan) {
            isManualEditRef.current = false;
          }
        }
      }
    } else {
      // Reset pesan jika form belum lengkap
      if (!jenisPermintaan && !golda && !jumlah && !tanggal && !lokasi) {
        setDraftPesan("");
        isManualEditRef.current = false;
        lastGeneratedPesanRef.current = "";
      }
    }
  }, [
    jenisPermintaan,
    nama,
    golda,
    jumlah,
    tanggal,
    lokasi,
    alasan,
    listGolda,
    listLokasi,
  ]);

  const getListGolda = async () => {
    var data1 = [];

    try {
      const response = await axios.get(API_DONOR_DARAH + "bloodtypes");
      data1 = response.data.data;

      if (data1.length > 0) {
        // Urutkan: A, B, AB, O tanpa rhesus di atas, kemudian yang dengan rhesus
        const golonganDasar = ["A", "B", "AB", "O"];
        const sorted = data1.sort((a, b) => {
          const golA = a.golonganDarah.split("(")[0].trim();
          const golB = b.golonganDarah.split("(")[0].trim();
          const indexA = golonganDasar.indexOf(golA);
          const indexB = golonganDasar.indexOf(golB);

          // Jika keduanya ada di golongan dasar
          if (indexA !== -1 && indexB !== -1) {
            // Bandingkan index
            if (indexA !== indexB) {
              return indexA - indexB;
            }
            // Jika sama, yang tanpa rhesus di atas
            const hasRhesusA = a.golonganDarah.includes("(");
            const hasRhesusB = b.golonganDarah.includes("(");
            if (hasRhesusA && !hasRhesusB) return 1;
            if (!hasRhesusA && hasRhesusB) return -1;
            return a.golonganDarah.localeCompare(b.golonganDarah);
          }

          // Jika hanya A yang ada di golongan dasar
          if (indexA !== -1) return -1;
          // Jika hanya B yang ada di golongan dasar
          if (indexB !== -1) return 1;
          // Keduanya tidak ada di golongan dasar, urutkan alphabetically
          return a.golonganDarah.localeCompare(b.golonganDarah);
        });

        setListGolda(sorted);
      } else {
        setListGolda([]);
      }
      // berita.gambarKu = berita.gambar.split(',');
      // berita.isiKu = berita.isi.split('##');
    } catch (error) {
      console.error(error);
    }
  };

  const getListLokasi = async () => {
    var data1 = [];

    try {
      const response = await axios.get(API_DONOR_DARAH + "lokasi");
      data1 = response.data.data;

      if (data1.length > 0) {
        setListLokasi(data1);
        // Set default lokasi ke Rumah Sakit jika ada dan belum di-set
        if (!lokasi) {
          const rumahSakit = data1.find(
            (item) =>
              item.lokasi &&
              (item.lokasi.toLowerCase().includes("rumah sakit") ||
                item.lokasi.toLowerCase().includes("rsud"))
          );
          if (rumahSakit) {
            setLokasi(rumahSakit.id);
          }
        }
      }
      // berita.gambarKu = berita.gambar.split(',');
      // berita.isiKu = berita.isi.split('##');
    } catch (error) {
      console.error(error);
    }
  };

  const kirimPermintaan = async () => {
    const tgl = new Date(tanggal);
    const year = tgl.getFullYear();
    const month = String(tgl.getMonth() + 1).padStart(2, "0");
    const day = String(tgl.getDate()).padStart(2, "0");
    const date_string = `${year}-${month}-${day}`;

    // Jika bank darah, nama otomatis "Bank Darah"
    const namaFinal = jenisPermintaan === "bank_darah" ? "Bank Darah" : nama;

    const data = {
      namaPenerima: namaFinal,
      penerimaID: currentUser.uid,
      tanggalPermintaan: date_string,
      bloodTypeID: golda,
      jumlahDibutuhkan: jumlah,
      status: "belum_terpenuhi",
      desa: "",
      alasan: alasan,
      lokasi: lokasi,
    };

    try {
      const response = await axios({
        method: "post",
        url: API_DONOR_DARAH + "donationrequests",
        timeout: API_TIMEOUT,
        data: data,
      });

      if (response.status === 200 && response.data?.success) {
        const requestID =
          response.data.data.insertId || response.data.data.requestID;
        setPermintaanID(requestID);

        // Generate draft pesan setelah permintaan berhasil disimpan
        generateDraftPesan();

        // Aktifkan tombol Kirim Pesan
        setCanSendMessage(true);

        swal("Berhasil", "Permintaan darah berhasil disimpan", "success");
      } else {
        swal("Gagal", "Data Permintaan gagal diinput", "error");
      }
    } catch (error) {
      console.error("Error menyimpan permintaan:", error);
      swal("Gagal", "Data Permintaan Darah gagal diinput", "error");
    }
  };

  // Fungsi untuk generate draft pesan
  const generateDraftPesan = async () => {
    try {
      // Jika bank darah, nama otomatis "Bank Darah"
      const namaFinal = jenisPermintaan === "bank_darah" ? "Bank Darah" : nama;

      // Ambil info golongan darah yang dipilih
      const selectedBloodType = listGolda.find((bt) => bt.bloodTypeID == golda);
      const golonganDarahName = selectedBloodType
        ? selectedBloodType.golonganDarah
        : "Golongan Darah";

      // Ambil info lokasi
      const lokasiIDNum = parseInt(lokasi);
      const selectedLokasi = listLokasi.find(
        (loc) => parseInt(loc.id) === lokasiIDNum
      );
      const lokasiName = selectedLokasi
        ? selectedLokasi.lokasi
        : "Lokasi Donor";

      // Format tanggal
      const tgl = new Date(tanggal);
      const tanggalFormatted = tgl.toLocaleDateString("id-ID", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      let pesan = "";

      // Jika Bank Darah, gunakan pesan khusus
      if (jenisPermintaan === "bank_darah") {
        pesan = `🩸 *HIMBAUAN DONOR DARAH* 🩸

Halo *[Nama Pendonor]* Pendonor Darah,

Kami mengundang Bapak/Ibu untuk berpartisipasi dalam kegiatan donor darah untuk mengisi stok darah pada Bank Darah di RSUD Ba'a.

📋 *Detail Permintaan:*
• Golongan Darah: *${golonganDarahName}*
• Jumlah Kantung: *${jumlah} kantong*
• Untuk: *Bank Darah*
• Tanggal: *${tanggalFormatted}*
• Lokasi: *${lokasiName}*
• Tujuan: *Pendonor Sukarela (untuk permintaan bank darah)*

🔗 *Form Response:*
[LINK_RESPONSE]

Silakan klik link di atas untuk mengisi form response jika Anda bersedia membantu.

Terima kasih atas kebaikan hati Anda.

🙏🙏🙏
*Sistem Donor Darah - Dinkes Kab. Rote Ndao*`;
      } else {
        // Jika Pasien, gunakan pesan seperti biasa
        const tujuanText =
          alasan == 1
            ? "Pendonor Pengganti (untuk permintaan pasien)"
            : "Pendonor Sukarela (untuk permintaan bank darah)";

        pesan = `🩸 *PERMINTAAN DARAH URGENT* 🩸

Halo *[Nama Pendonor]* Pendonor Darah,

Kami membutuhkan bantuan Anda untuk mendonorkan darah dengan detail sebagai berikut:

📋 *Detail Permintaan:*
• Golongan Darah: *${golonganDarahName}*
• Jumlah Kantung: *${jumlah} kantong*
• Untuk: *${namaFinal}*
• Tanggal: *${tanggalFormatted}*
• Lokasi: *${lokasiName}*
• Tujuan: *${tujuanText}*

🔗 *Form Response:*
[LINK_RESPONSE]

Silakan klik link di atas untuk mengisi form response jika Anda bersedia membantu.

Terima kasih atas kebaikan hati Anda.

🙏🙏🙏
*Sistem Donor Darah - Dinkes Kab. Rote Ndao*`;
      }

      setDraftPesan(pesan);
      isManualEditRef.current = false;
    } catch (error) {
      console.error("Error generating draft pesan:", error);
    }
  };

  const ambilPendonorDanTampilkanModal = async (
    bloodTypeID,
    namaPenerima,
    jumlahKantung,
    tanggalPermintaan,
    lokasiID,
    requestID = null
  ) => {
    try {
      // Ambil semua data pendonor
      const response = await axios.get(API_DONOR_DARAH + "donatur", {
        timeout: API_TIMEOUT,
      });

      if (response.data?.success && response.data.data) {
        // Ambil info golongan darah yang dipilih
        const selectedBloodType = listGolda.find(
          (bt) => bt.bloodTypeID == bloodTypeID
        );
        const golonganDarahName = selectedBloodType
          ? selectedBloodType.golonganDarah
          : "Golongan Darah";

        // Filter pendonor berdasarkan golongan darah
        // Bandingkan dengan golongan dasar (A, B, AB, O) tanpa rhesus
        const filteredPendonor = response.data.data.filter((donor) => {
          if (!donor.bloodTypes || !donor.noHp) return false;

          // Ambil golongan dasar dari bloodTypes (misal "A(+)" -> "A")
          const donorGolDasar = donor.bloodTypes.split("(")[0].trim();
          const selectedGolDasar = golonganDarahName.split("(")[0].trim();

          return donorGolDasar === selectedGolDasar;
        });

        // Ambil info lokasi
        const lokasiIDNum = parseInt(lokasiID);
        const selectedLokasi = listLokasi.find(
          (loc) => parseInt(loc.id) === lokasiIDNum
        );
        const lokasiName = selectedLokasi
          ? selectedLokasi.lokasi
          : "Lokasi Donor";

        // Format tanggal untuk pesan
        const tgl = new Date(tanggalPermintaan);
        const tanggalFormatted = tgl.toLocaleDateString("id-ID", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });

        // Set state untuk modal
        setSelectedGolda(bloodTypeID);
        setSelectedGoldaName(golonganDarahName);
        setJumlahKantung(jumlahKantung);
        setNamaPenerima(namaPenerima);
        setTanggalPermintaan(tanggalFormatted);
        setLokasiDonor(lokasiName);
        setListPendonor(filteredPendonor);

        // Set requestID jika ada
        if (requestID) {
          setPermintaanID(requestID);
        }

        // Buka modal konfirmasi (tidak langsung kirim)
        setModalWhatsAppOpen(true);
      } else {
        swal("Info", "Data pendonor tidak ditemukan", "info");
        resetForm();
      }
    } catch (error) {
      console.error("Error mengambil data pendonor:", error);
      swal("Error", "Gagal mengambil data pendonor", "error");
      resetForm();
    }
  };

  const ambilPendonorDanKirimPesan = async (
    bloodTypeID,
    namaPenerima,
    jumlahKantung,
    tanggalPermintaan,
    lokasiID,
    requestID
  ) => {
    try {
      // Ambil semua data pendonor
      const response = await axios.get(API_DONOR_DARAH + "donatur", {
        timeout: API_TIMEOUT,
      });

      if (response.data?.success && response.data.data) {
        // Ambil info golongan darah yang dipilih
        const selectedBloodType = listGolda.find(
          (bt) => bt.bloodTypeID == bloodTypeID
        );
        const golonganDarahName = selectedBloodType
          ? selectedBloodType.golonganDarah
          : "Golongan Darah";

        // Filter pendonor berdasarkan golongan darah
        const filteredPendonor = response.data.data.filter((donor) => {
          if (!donor.bloodTypes || !donor.noHp) return false;
          const donorGolDasar = donor.bloodTypes.split("(")[0].trim();
          const selectedGolDasar = golonganDarahName.split("(")[0].trim();
          return donorGolDasar === selectedGolDasar;
        });

        // Ambil info lokasi
        const lokasiIDNum = parseInt(lokasiID);
        const selectedLokasi = listLokasi.find(
          (loc) => parseInt(loc.id) === lokasiIDNum
        );
        const lokasiName = selectedLokasi
          ? selectedLokasi.lokasi
          : "Lokasi Donor";

        // Format tanggal untuk pesan
        const tgl = new Date(tanggalPermintaan);
        const tanggalFormatted = tgl.toLocaleDateString("id-ID", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });

        // Set state untuk modal
        setSelectedGolda(bloodTypeID);
        setSelectedGoldaName(golonganDarahName);
        setJumlahKantung(jumlahKantung);
        setNamaPenerima(namaPenerima);
        setTanggalPermintaan(tanggalFormatted);
        setLokasiDonor(lokasiName);
        setListPendonor(filteredPendonor);
        setPermintaanID(requestID);

        // Langsung kirim pesan tanpa modal konfirmasi dulu
        await kirimWhatsAppLangsung(filteredPendonor, requestID);

        // Setelah selesai, tampilkan modal dengan hasil
        setModalWhatsAppOpen(true);
      } else {
        swal("Info", "Data pendonor tidak ditemukan", "info");
        resetForm();
      }
    } catch (error) {
      console.error("Error mengambil data pendonor:", error);
      swal("Error", "Gagal mengambil data pendonor", "error");
      resetForm();
    }
  };

  // Fungsi untuk mengirim pesan WhatsApp langsung (dengan batch processing)
  const kirimWhatsAppLangsung = async (
    pendonorList,
    requestID,
    batchIndex = 0,
    isContinue = false,
    batchesData = null
  ) => {
    try {
      console.log("kirimWhatsAppLangsung dipanggil", {
        batchIndex,
        isContinue,
        pendonorListLength: pendonorList.length,
        requestID,
        draftPesan: !!draftPesan,
      });

      if (pendonorList.length === 0) {
        swal("Info", "Tidak ada pendonor yang akan dikirimi pesan", "info");
        return;
      }

      if (!draftPesan || draftPesan.trim() === "") {
        swal("Error", "Draft pesan tidak ditemukan", "error");
        return;
      }

      let batches = batchesData;
      let totalBatchesCount = 0;

      // Setup batches jika mulai dari awal
      if (!isContinue || !batches) {
        const validDonors = pendonorList.filter(
          (donor) => donor.noHp && donor.noHp.trim() !== ""
        );

        if (validDonors.length === 0) {
          swal("Info", "Tidak ada pendonor dengan nomor HP yang valid", "info");
          return;
        }

        // Split menjadi batch maksimal 50
        const BATCH_SIZE = 50;
        batches = [];
        for (let i = 0; i < validDonors.length; i += BATCH_SIZE) {
          batches.push(validDonors.slice(i, i + BATCH_SIZE));
        }

        totalBatchesCount = batches.length;
        setAllBatches(batches);
        setCurrentBatchIndex(0);
        setBatchProgress({ current: 1, total: totalBatchesCount });

        // Jika lebih dari 50 pendonor, tampilkan info
        if (validDonors.length > BATCH_SIZE) {
          const confirm = await swal({
            title: "Peringatan",
            text: `Total ${validDonors.length} pendonor akan dikirimi pesan dalam ${totalBatchesCount} batch (maksimal ${BATCH_SIZE} per batch). Batch pertama akan dikirim sekarang.`,
            icon: "info",
            buttons: {
              cancel: "Batal",
              confirm: "Lanjutkan",
            },
          });

          if (!confirm) {
            return;
          }
        }
      } else {
        totalBatchesCount = batches.length;
      }

      // Ambil batch yang akan dikirim
      const currentBatch = batches[batchIndex];
      if (!currentBatch || currentBatch.length === 0) {
        setSendingWhatsApp(false);
        return;
      }

      setSendingWhatsApp(true);
      cancelSendingRef.current = false;
      setProgressWhatsApp({ current: 0, total: currentBatch.length });
      setBatchProgress({ current: batchIndex + 1, total: totalBatchesCount });

      // Reset detail pengiriman hanya untuk batch pertama
      if (batchIndex === 0) {
        setDetailPengiriman([]);
      }

      // Ambil konfigurasi wablas aktif dari database
      let url = wablasConfig.url;
      let Tokenwhatsap = wablasConfig.tokenwhatsapp;
      let SecretKey = wablasConfig.secret_key || "";

      // Jika belum ada konfigurasi, ambil dari database
      if (!url || !Tokenwhatsap) {
        try {
          const configResponse = await axios.get(
            `${API_DONOR_DARAH}wablas/active`,
            {
              timeout: API_TIMEOUT,
            }
          );
          if (configResponse.data?.success && configResponse.data.data) {
            url = configResponse.data.data.url;
            Tokenwhatsap = configResponse.data.data.tokenwhatsapp;
            SecretKey = configResponse.data.data.secret_key || "";
            setWablasConfig({
              url,
              tokenwhatsapp: Tokenwhatsap,
              secret_key: SecretKey,
            });
          }
        } catch (error) {
          console.error("Error fetching wablas config:", error);
          swal("Error", "Gagal mengambil konfigurasi Wablas", "error");
          setSendingWhatsApp(false);
          return;
        }
      }

      let successCount = 0;
      let failCount = 0;
      const validDonors = currentBatch;
      const totalDonors = validDonors.length;

      // Ambil info tujuan dari alasan
      const tujuanText =
        alasan == 1
          ? "Pendonor Pengganti (untuk permintaan pasien)"
          : "Pendonor Sukarela (untuk permintaan bank darah)";

      // Array untuk menyimpan detail pengiriman (untuk disimpan ke database)
      const detailPengirimanForDB = [];

      // Kirim pesan ke setiap pendonor dengan delay untuk menghindari rate limiting
      for (let i = 0; i < validDonors.length; i++) {
        // Check jika pengiriman dibatalkan
        if (cancelSendingRef.current) {
          break;
        }

        const donor = validDonors[i];

        let statusPengiriman = "Mengirim...";
        let responseMessage = "";
        let pesanPersonal = "";

        // Format nomor HP (diluar try agar bisa digunakan di catch)
        let phoneNumber = "";
        if (donor.noHp) {
          phoneNumber = donor.noHp.replace(/\D/g, "");
          if (phoneNumber.startsWith("0")) {
            phoneNumber = "62" + phoneNumber.substring(1);
          } else if (!phoneNumber.startsWith("62")) {
            phoneNumber = "62" + phoneNumber;
          }
        }

        try {
          // Buat link unik untuk setiap pendonor
          const baseUrl = window.location.origin;
          const responseLink = `${baseUrl}/#/response-permintaan?donaturId=${donor.id}&requestId=${requestID}`;

          // Personalisasi pesan dengan nama pendonor
          pesanPersonal = draftPesan.replace(
            /Halo \*\[Nama Pendonor\]\* Pendonor Darah,/g,
            `Halo *${donor.nama || "Bapak/Ibu"}* Pendonor Darah,`
          );

          // Fallback: jika format lama masih ada
          if (pesanPersonal.includes("Halo Bapak/Ibu Pendonor Darah,")) {
            pesanPersonal = pesanPersonal.replace(
              "Halo Bapak/Ibu Pendonor Darah,",
              `Halo *${donor.nama || "Bapak/Ibu"}* Pendonor Darah,`
            );
          }

          // Ganti placeholder link dengan link unik
          pesanPersonal = pesanPersonal.replace(
            "[LINK_RESPONSE]",
            responseLink
          );

          // Pastikan tujuan sudah ada di pesan (jika belum ada, tambahkan)
          if (!pesanPersonal.includes("• Tujuan:")) {
            const lokasiIndex = pesanPersonal.indexOf("• Lokasi:");
            if (lokasiIndex !== -1) {
              const lokasiLineEnd = pesanPersonal.indexOf("\n", lokasiIndex);
              pesanPersonal =
                pesanPersonal.substring(0, lokasiLineEnd) +
                `\n• Tujuan: *${tujuanText}*` +
                pesanPersonal.substring(lokasiLineEnd);
            }
          }

          console.log("Mengirim pesan ke:", {
            url: url,
            phone: phoneNumber,
            messageLength: pesanPersonal.length,
            hasSecretKey: !!SecretKey,
          });

          // Kirim pesan melalui backend endpoint
          const response = await axios.post(
            `${API_DONOR_DARAH}wablas/send-message`,
            {
              url: url,
              token: Tokenwhatsap,
              secret_key: SecretKey,
              phone: phoneNumber,
              message: pesanPersonal,
              flag: "instant",
            },
            {
              timeout: API_TIMEOUT,
            }
          );

          console.log("Response dari backend:", {
            status: response.status,
            data: response.data,
          });

          if (response.data?.success === 1) {
            successCount++;
            statusPengiriman = "Berhasil";
            responseMessage =
              response.data?.message ||
              response.data?.data?.message ||
              "Pesan berhasil dikirim";
            console.log("✅ Pesan berhasil dikirim ke", donor.nama);
          } else {
            failCount++;
            statusPengiriman = "Gagal";
            responseMessage = response.data?.message || "Gagal mengirim pesan";
            console.error(`❌ Gagal mengirim ke ${donor.nama}:`, response.data);
          }
        } catch (error) {
          failCount++;
          statusPengiriman = "Error";
          if (error.response) {
            responseMessage =
              error.response.data?.message ||
              error.response.statusText ||
              "Gagal mengirim pesan";
            console.error(
              `Error mengirim ke ${donor.nama}:`,
              error.response.data
            );
          } else if (error.request) {
            responseMessage = "Tidak ada response dari server";
            console.error(`Error mengirim ke ${donor.nama}:`, error.request);
          } else {
            responseMessage = error.message || "Terjadi kesalahan";
            console.error(`Error mengirim ke ${donor.nama}:`, error.message);
          }
        }

        // Tentukan status untuk database
        const statusForDB =
          statusPengiriman === "Berhasil"
            ? "berhasil"
            : statusPengiriman === "Error"
            ? "error"
            : "gagal";

        // Simpan detail pengiriman untuk state dan database
        const detailItem = {
          nama: donor.nama || "-",
          tujuan: tujuanText,
          noHp: donor.noHp || "-",
          pesan: pesanPersonal,
          status: statusPengiriman,
          response: responseMessage,
          // Data untuk disimpan ke database
          data_donor_id: donor.id || null,
          nomor_hp: phoneNumber,
          nama_pendonor: donor.nama || null,
          status_db: statusForDB,
          response_message: responseMessage,
        };

        setDetailPengiriman((prev) => [...prev, detailItem]);

        // Simpan juga ke array untuk database
        detailPengirimanForDB.push({
          data_donor_id: donor.id || null,
          nomor_hp: phoneNumber,
          nama_pendonor: donor.nama || null,
          pesan: pesanPersonal,
          status: statusForDB,
          response_message: responseMessage,
        });

        // Update progress
        setProgressWhatsApp({ current: i + 1, total: totalDonors });

        // Delay antar pengiriman
        if (i < validDonors.length - 1 && !cancelSendingRef.current) {
          if ((i + 1) % 10 === 0) {
            await delay(5000);
          } else {
            await delay(2000 + Math.random() * 1000);
          }
        }
      }

      setSendingWhatsApp(false);
      setProgressWhatsApp({ current: 0, total: 0 });

      // Simpan data pengiriman ke database untuk batch ini
      if (!cancelSendingRef.current && detailPengirimanForDB.length > 0) {
        try {
          const saveResponse = await axios.post(
            `${API_DONOR_DARAH}pengiriman-pesan`,
            {
              edukasi_id: null, // Permintaan donor tidak punya edukasi_id
              jenis_pesan: "permintaan_donor",
              created_by:
                (currentUser && typeof currentUser === "object"
                  ? currentUser.uid
                  : null) || null,
              total_pendonor: detailPengirimanForDB.length,
              details: detailPengirimanForDB,
            },
            {
              timeout: API_TIMEOUT,
            }
          );

          if (saveResponse.data?.success === 1) {
            console.log(
              `✅ Data pengiriman batch ${
                batchIndex + 1
              } berhasil disimpan ke database`
            );
          } else {
            console.error(
              "❌ Gagal menyimpan data pengiriman:",
              saveResponse.data?.message
            );
          }
        } catch (error) {
          console.error(
            "❌ Error menyimpan data pengiriman ke database:",
            error
          );
        }
      }

      // Cek apakah masih ada batch berikutnya
      const hasNextBatch = batchIndex + 1 < batches.length;

      // Tampilkan hasil dan opsi untuk melanjutkan
      if (!cancelSendingRef.current) {
        if (hasNextBatch) {
          // Masih ada batch berikutnya, tanyakan apakah ingin melanjutkan
          const continueSending = await swal({
            title: `Batch ${batchIndex + 1} Selesai`,
            text: `Batch ${batchIndex + 1}/${
              batches.length
            } selesai. Berhasil: ${successCount}, Gagal: ${failCount}. Apakah ingin melanjutkan ke batch ${
              batchIndex + 2
            }?`,
            icon: "info",
            buttons: {
              cancel: "Berhenti",
              confirm: "Lanjutkan",
            },
          });

          if (continueSending) {
            // Lanjutkan ke batch berikutnya
            setCurrentBatchIndex(batchIndex + 1);
            await kirimWhatsAppLangsung(
              pendonorList,
              requestID,
              batchIndex + 1,
              true,
              batches
            );
            return;
          } else {
            // User memilih berhenti
            if (successCount > 0) {
              swal(
                "Pengiriman Dihentikan",
                `Batch ${batchIndex + 1}/${
                  batches.length
                } telah dikirim. Berhasil: ${successCount}, Gagal: ${failCount}`,
                "info"
              );
            }
          }
        } else {
          // Semua batch selesai
          if (successCount > 0) {
            swal(
              "Berhasil",
              `Semua batch selesai! Total berhasil: ${successCount} pendonor${
                failCount > 0 ? `, gagal: ${failCount}` : ""
              }`,
              "success"
            );
          } else {
            swal("Gagal", "Gagal mengirim pesan ke semua pendonor", "error");
          }
        }
      } else {
        cancelSendingRef.current = false;
      }
    } catch (error) {
      console.error("Error di kirimWhatsAppLangsung:", error);
      setSendingWhatsApp(false);
      swal(
        "Error",
        "Terjadi kesalahan saat mengirim pesan: " +
          (error.message || "Unknown error"),
        "error"
      );
    }
  };

  const resetForm = () => {
    setLokasi("");
    setAlasan("");
    setJumlah("");
    setNama("");
    setTanggal("");
    setGolda("");
    setJenisPermintaan("");
    setDraftPesan("");
    setDetailPengiriman([]);
    setCanSendMessage(false);
    setPermintaanID(null);
    isManualEditRef.current = false;
    lastGeneratedPesanRef.current = "";
  };

  // Fungsi untuk mengirim pesan WhatsApp (dengan batch processing)
  const kirimWhatsApp = async (
    batchIndex = 0,
    isContinue = false,
    batchesData = null
  ) => {
    try {
      console.log("kirimWhatsApp dipanggil", {
        batchIndex,
        isContinue,
        listPendonorLength: listPendonor.length,
        permintaanID,
        draftPesan: !!draftPesan,
      });

      if (listPendonor.length === 0 || !permintaanID) {
        swal("Info", "Tidak ada pendonor yang akan dikirimi pesan", "info");
        return;
      }

      if (!draftPesan || draftPesan.trim() === "") {
        swal("Error", "Draft pesan tidak ditemukan", "error");
        return;
      }

      let batches = batchesData;
      let totalBatchesCount = 0;

      // Setup batches jika mulai dari awal
      if (!isContinue || !batches) {
        const validDonors = listPendonor.filter(
          (donor) => donor.noHp && donor.noHp.trim() !== ""
        );

        if (validDonors.length === 0) {
          swal("Info", "Tidak ada pendonor dengan nomor HP yang valid", "info");
          return;
        }

        // Split menjadi batch maksimal 50
        const BATCH_SIZE = 50;
        batches = [];
        for (let i = 0; i < validDonors.length; i += BATCH_SIZE) {
          batches.push(validDonors.slice(i, i + BATCH_SIZE));
        }

        totalBatchesCount = batches.length;
        setAllBatches(batches);
        setCurrentBatchIndex(0);
        setBatchProgress({ current: 1, total: totalBatchesCount });

        // Jika lebih dari 50 pendonor, tampilkan info
        if (validDonors.length > BATCH_SIZE) {
          const confirm = await swal({
            title: "Peringatan",
            text: `Total ${validDonors.length} pendonor akan dikirimi pesan dalam ${totalBatchesCount} batch (maksimal ${BATCH_SIZE} per batch). Batch pertama akan dikirim sekarang.`,
            icon: "info",
            buttons: {
              cancel: "Batal",
              confirm: "Lanjutkan",
            },
          });

          if (!confirm) {
            return;
          }
        }
      } else {
        totalBatchesCount = batches.length;
      }

      // Ambil batch yang akan dikirim
      const currentBatch = batches[batchIndex];
      if (!currentBatch || currentBatch.length === 0) {
        setSendingWhatsApp(false);
        return;
      }

      setSendingWhatsApp(true);
      cancelSendingRef.current = false;
      setProgressWhatsApp({ current: 0, total: currentBatch.length });
      setBatchProgress({ current: batchIndex + 1, total: totalBatchesCount });

      // Reset detail pengiriman hanya untuk batch pertama
      if (batchIndex === 0) {
        setDetailPengiriman([]);
      }

      // Konfigurasi wablas sekarang diambil langsung oleh backend dari database
      // Tidak perlu dikirim dari frontend untuk keamanan

      let successCount = 0;
      let failCount = 0;
      const validDonors = currentBatch;

      // Ambil info tujuan dari alasan
      const tujuanText =
        alasan == 1
          ? "Pendonor Pengganti (untuk permintaan pasien)"
          : "Pendonor Sukarela (untuk permintaan bank darah)";

      // Array untuk menyimpan detail pengiriman (untuk disimpan ke database)
      const detailPengirimanForDB = [];

      // Check jika pengiriman dibatalkan
      if (cancelSendingRef.current) {
        setSendingWhatsApp(false);
        return;
      }

      // Siapkan data untuk multiple send (API V2)
      const recipients = [];
      const donorMap = new Map(); // Untuk mapping phone -> donor data

      // Format nomor HP dan personalisasi pesan untuk setiap pendonor
      for (let i = 0; i < validDonors.length; i++) {
        const donor = validDonors[i];

        // Format nomor HP
        let phoneNumber = "";
        if (donor.noHp) {
          phoneNumber = donor.noHp.replace(/\D/g, "");
          if (phoneNumber.startsWith("0")) {
            phoneNumber = "62" + phoneNumber.substring(1);
          } else if (!phoneNumber.startsWith("62")) {
            phoneNumber = "62" + phoneNumber;
          }
        }

        if (!phoneNumber) continue;

        // Buat link unik untuk setiap pendonor
        const baseUrl = window.location.origin;
        const responseLink = `${baseUrl}/#/response-permintaan?donaturId=${donor.id}&requestId=${permintaanID}`;

        // Personalisasi pesan dengan nama pendonor
        let pesanPersonal = draftPesan.replace(
          /Halo \*\[Nama Pendonor\]\* Pendonor Darah,/g,
          `Halo *${donor.nama || "Bapak/Ibu"}* Pendonor Darah,`
        );

        // Fallback: jika format lama masih ada
        if (pesanPersonal.includes("Halo Bapak/Ibu Pendonor Darah,")) {
          pesanPersonal = pesanPersonal.replace(
            "Halo Bapak/Ibu Pendonor Darah,",
            `Halo *${donor.nama || "Bapak/Ibu"}* Pendonor Darah,`
          );
        }

        // Ganti placeholder link dengan link unik
        pesanPersonal = pesanPersonal.replace(
          "[LINK_RESPONSE]",
          responseLink
        );

        // Pastikan tujuan sudah ada di pesan (jika belum ada, tambahkan)
        if (!pesanPersonal.includes("Tujuan:")) {
          pesanPersonal = pesanPersonal.replace(
            "📋 *Detail Permintaan:*",
            `📋 *Detail Permintaan:*
• Tujuan: *${tujuanText}*`
          );
        }

        // Tambahkan ke recipients
        recipients.push({
          phone: phoneNumber,
          message: pesanPersonal,
        });

        // Simpan mapping untuk tracking
        donorMap.set(phoneNumber, {
          donor,
          pesanPersonal,
        });
      }

      if (recipients.length === 0) {
        swal("Info", "Tidak ada nomor HP yang valid untuk dikirim", "info");
        setSendingWhatsApp(false);
        return;
      }

      // Update progress awal
      setProgressWhatsApp({ current: 0, total: recipients.length });

      // Kirim semua pesan sekaligus menggunakan API V2
      try {
        console.log("Mengirim multiple pesan via API V2:", {
          recipientCount: recipients.length,
        });

        // Update progress: sedang mengirim
        recipients.forEach((_, index) => {
          const phoneNumber = recipients[index].phone;
          const donorData = donorMap.get(phoneNumber);
          if (donorData) {
            const detailItem = {
              nama: donorData.donor.nama || "-",
              tujuan: tujuanText,
              noHp: donorData.donor.noHp || "-",
              pesan: donorData.pesanPersonal,
              status: "Mengirim...",
              response: "",
            };
            setDetailPengiriman((prev) => [...prev, detailItem]);
          }
        });

        // Kirim melalui backend endpoint untuk multiple send (API V2)
        const response = await axios.post(
          `${API_DONOR_DARAH}wablas/send-multiple-message`,
          {
            recipients: recipients,
          },
          {
            timeout: 60000, // 60 detik untuk multiple send
          }
        );

        console.log("Response dari backend (API V2):", {
          status: response.status,
          data: response.data,
        });

        // Update progress: selesai
        setProgressWhatsApp({ current: recipients.length, total: recipients.length });

        // Proses hasil pengiriman
        if (response.data?.success === 1) {
          // Parse hasil per-pesan dari backend
          const results = response.data?.results || [];
          
          // Jika ada hasil per-pesan, gunakan itu
          if (results.length > 0) {
            results.forEach((result) => {
              const phoneNumber = result.phone;
              const donorData = donorMap.get(phoneNumber);
              
              if (donorData) {
                const isSuccess = result.status === "success";
                const statusForDB = isSuccess ? "berhasil" : "gagal";
                const statusDisplay = isSuccess ? "Berhasil" : "Gagal";
                const responseMessage = result.message || (isSuccess ? "Pesan berhasil dikirim" : "Gagal mengirim pesan");
                
                if (isSuccess) {
                  successCount++;
                } else {
                  failCount++;
                }

                // Update detail di state
                setDetailPengiriman((prev) => {
                  const updated = [...prev];
                  const index = updated.findIndex(
                    (item) => item.noHp === donorData.donor.noHp
                  );
                  if (index !== -1) {
                    updated[index] = {
                      ...updated[index],
                      status: statusDisplay,
                      response: responseMessage,
                    };
                  }
                  return updated;
                });

                // Simpan ke array untuk database
                detailPengirimanForDB.push({
                  data_donor_id: donorData.donor.id || null,
                  nomor_hp: phoneNumber,
                  nama_pendonor: donorData.donor.nama || null,
                  pesan: donorData.pesanPersonal,
                  status: statusForDB,
                  response_message: responseMessage,
                });
              }
            });
            
            console.log(`✅ Pengiriman selesai. Berhasil: ${successCount}, Gagal: ${failCount}`);
          } else {
            // Fallback: jika tidak ada hasil per-pesan, gunakan data umum
            const generalSuccess = response.data?.successCount > 0 || response.data?.successCount === undefined;
            
            if (generalSuccess) {
              successCount = response.data?.successCount || recipients.length;
              failCount = response.data?.failCount || 0;
              
              recipients.forEach((recipient) => {
                const phoneNumber = recipient.phone;
                const donorData = donorMap.get(phoneNumber);
                if (donorData) {
                  const statusForDB = "berhasil";
                  const responseMessage = response.data?.message || "Pesan berhasil dikirim";

                  // Update detail di state
                  setDetailPengiriman((prev) => {
                    const updated = [...prev];
                    const index = updated.findIndex(
                      (item) => item.noHp === donorData.donor.noHp
                    );
                    if (index !== -1) {
                      updated[index] = {
                        ...updated[index],
                        status: "Berhasil",
                        response: responseMessage,
                      };
                    }
                    return updated;
                  });

                  // Simpan ke array untuk database
                  detailPengirimanForDB.push({
                    data_donor_id: donorData.donor.id || null,
                    nomor_hp: phoneNumber,
                    nama_pendonor: donorData.donor.nama || null,
                    pesan: donorData.pesanPersonal,
                    status: statusForDB,
                    response_message: responseMessage,
                  });
                }
              });
            } else {
              failCount = recipients.length;
              successCount = 0;
              
              const errorMessage = response.data?.message || "Gagal mengirim pesan";
              
              recipients.forEach((recipient) => {
                const phoneNumber = recipient.phone;
                const donorData = donorMap.get(phoneNumber);
                if (donorData) {
                  const statusForDB = "gagal";

                  // Update detail di state
                  setDetailPengiriman((prev) => {
                    const updated = [...prev];
                    const index = updated.findIndex(
                      (item) => item.noHp === donorData.donor.noHp
                    );
                    if (index !== -1) {
                      updated[index] = {
                        ...updated[index],
                        status: "Gagal",
                        response: errorMessage,
                      };
                    }
                    return updated;
                  });

                  // Simpan ke array untuk database
                  detailPengirimanForDB.push({
                    data_donor_id: donorData.donor.id || null,
                    nomor_hp: phoneNumber,
                    nama_pendonor: donorData.donor.nama || null,
                    pesan: donorData.pesanPersonal,
                    status: statusForDB,
                    response_message: errorMessage,
                  });
                }
              });
            }
          }
        } else {
          // Gagal semua
          failCount = recipients.length;
          successCount = 0;

          const errorMessage =
            response.data?.message || "Gagal mengirim pesan";

          // Update detail pengiriman
          recipients.forEach((recipient) => {
            const phoneNumber = recipient.phone;
            const donorData = donorMap.get(phoneNumber);
            if (donorData) {
              const statusForDB = "gagal";

              // Update detail di state
              setDetailPengiriman((prev) => {
                const updated = [...prev];
                const index = updated.findIndex(
                  (item) => item.noHp === donorData.donor.noHp
                );
                if (index !== -1) {
                  updated[index] = {
                    ...updated[index],
                    status: "Gagal",
                    response: errorMessage,
                  };
                }
                return updated;
              });

              // Simpan ke array untuk database
              detailPengirimanForDB.push({
                data_donor_id: donorData.donor.id || null,
                nomor_hp: phoneNumber,
                nama_pendonor: donorData.donor.nama || null,
                pesan: donorData.pesanPersonal,
                status: statusForDB,
                response_message: errorMessage,
              });
            }
          });

          console.error(`❌ Gagal mengirim semua pesan:`, response.data);
        }
      } catch (error) {
        // Error saat mengirim
        failCount = recipients.length;
        successCount = 0;

        let errorMessage = "Terjadi kesalahan saat mengirim pesan";
        if (error.response) {
          errorMessage =
            error.response.data?.message ||
            error.response.statusText ||
            "Gagal mengirim pesan";
          console.error("Error response:", error.response.data);
        } else if (error.request) {
          errorMessage = "Tidak ada response dari server";
          console.error("Error request:", error.request);
        } else {
          errorMessage = error.message || "Terjadi kesalahan";
          console.error("Error:", error.message);
        }

        // Update detail pengiriman untuk semua
        recipients.forEach((recipient) => {
          const phoneNumber = recipient.phone;
          const donorData = donorMap.get(phoneNumber);
          if (donorData) {
            const statusForDB = "error";

            // Update detail di state
            setDetailPengiriman((prev) => {
              const updated = [...prev];
              const index = updated.findIndex(
                (item) => item.noHp === donorData.donor.noHp
              );
              if (index !== -1) {
                updated[index] = {
                  ...updated[index],
                  status: "Error",
                  response: errorMessage,
                };
              }
              return updated;
            });

            // Simpan ke array untuk database
            detailPengirimanForDB.push({
              data_donor_id: donorData.donor.id || null,
              nomor_hp: phoneNumber,
              nama_pendonor: donorData.donor.nama || null,
              pesan: donorData.pesanPersonal,
              status: statusForDB,
              response_message: errorMessage,
            });
          }
        });

        // Update progress
        setProgressWhatsApp({ current: recipients.length, total: recipients.length });
      }

      setSendingWhatsApp(false);
      setProgressWhatsApp({ current: 0, total: 0 });

      // Simpan data pengiriman ke database untuk batch ini
      if (!cancelSendingRef.current && detailPengirimanForDB.length > 0) {
        try {
          const saveResponse = await axios.post(
            `${API_DONOR_DARAH}pengiriman-pesan`,
            {
              edukasi_id: null, // Permintaan donor tidak punya edukasi_id
              jenis_pesan: "permintaan_donor",
              created_by:
                (currentUser && typeof currentUser === "object"
                  ? currentUser.uid
                  : null) || null,
              total_pendonor: detailPengirimanForDB.length,
              details: detailPengirimanForDB,
            },
            {
              timeout: API_TIMEOUT,
            }
          );

          if (saveResponse.data?.success === 1) {
            console.log(
              `✅ Data pengiriman batch ${
                batchIndex + 1
              } berhasil disimpan ke database`
            );
          } else {
            console.error(
              "❌ Gagal menyimpan data pengiriman:",
              saveResponse.data?.message
            );
          }
        } catch (error) {
          console.error(
            "❌ Error menyimpan data pengiriman ke database:",
            error
          );
        }
      }

      // Cek apakah masih ada batch berikutnya
      const hasNextBatch = batchIndex + 1 < batches.length;

      // Tampilkan hasil dan opsi untuk melanjutkan
      if (!cancelSendingRef.current) {
        if (hasNextBatch) {
          // Masih ada batch berikutnya, tanyakan apakah ingin melanjutkan
          const continueSending = await swal({
            title: `Batch ${batchIndex + 1} Selesai`,
            text: `Batch ${batchIndex + 1}/${
              batches.length
            } selesai. Berhasil: ${successCount}, Gagal: ${failCount}. Apakah ingin melanjutkan ke batch ${
              batchIndex + 2
            }?`,
            icon: "info",
            buttons: {
              cancel: "Berhenti",
              confirm: "Lanjutkan",
            },
          });

          if (continueSending) {
            // Lanjutkan ke batch berikutnya
            setCurrentBatchIndex(batchIndex + 1);
            await kirimWhatsApp(batchIndex + 1, true, batches);
            return;
          } else {
            // User memilih berhenti - modal tetap terbuka agar user bisa lihat detail
            swal(
              "Pengiriman Dihentikan",
              `Batch ${batchIndex + 1}/${
                batches.length
              } telah dikirim. Berhasil: ${successCount}, Gagal: ${failCount}\n\nModal tetap terbuka untuk melihat detail pengiriman.`,
              "info"
            );
          }
        } else {
          // Semua batch selesai - modal tetap terbuka agar user bisa lihat detail
          const totalSent = successCount + failCount;
          
          if (successCount > 0 && failCount === 0) {
            // Semua berhasil
            swal(
              "Berhasil! ✅",
              `Semua pesan berhasil dikirim!\n\nTotal: ${totalSent} pendonor\nBerhasil: ${successCount}\nGagal: ${failCount}\n\nModal tetap terbuka untuk melihat detail pengiriman.`,
              "success"
            );
          } else if (successCount > 0 && failCount > 0) {
            // Sebagian berhasil
            swal(
              "Pengiriman Selesai ⚠️",
              `Pengiriman selesai dengan hasil:\n\nTotal: ${totalSent} pendonor\n✅ Berhasil: ${successCount}\n❌ Gagal: ${failCount}\n\nSilakan cek detail pengiriman di tabel di bawah ini.`,
              "warning"
            );
          } else {
            // Semua gagal
            swal(
              "Gagal ❌",
              `Gagal mengirim pesan ke semua pendonor.\n\nTotal: ${totalSent} pendonor\nBerhasil: ${successCount}\nGagal: ${failCount}\n\nSilakan cek detail pengiriman di tabel di bawah ini.`,
              "error"
            );
          }
        }
      } else {
        cancelSendingRef.current = false;
      }
    } catch (error) {
      console.error("Error di kirimWhatsApp:", error);
      setSendingWhatsApp(false);
      swal(
        "Error",
        "Terjadi kesalahan saat mengirim pesan: " +
          (error.message || "Unknown error"),
        "error"
      );
    }
  };

  const handleKirim = () => {
    if (jenisPermintaan && alasan && golda && lokasi && jumlah) {
      if (jenisPermintaan === "pasien" && !nama) {
        swal("Info", "Nama pasien harus diisi", "error");
        return;
      }
      kirimPermintaan();
    } else {
      swal("Info", "Semua data harus diisi", "error");
    }
  };

  // Fungsi untuk membuka modal konfirmasi (tidak langsung kirim)
  const handleKirimPesan = async () => {
    // Validasi form
    if (!nama || !alasan || !golda || !lokasi || !jumlah) {
      swal("Info", "Semua data form harus diisi", "error");
      return;
    }

    // Validasi draft pesan
    if (!draftPesan || draftPesan.trim() === "") {
      swal("Info", "Draft pesan tidak boleh kosong", "error");
      return;
    }

    // Validasi permintaan sudah disimpan
    if (!permintaanID) {
      swal("Info", "Silakan simpan permintaan terlebih dahulu", "error");
      return;
    }

    // Format tanggal untuk dikirim ke fungsi
    const tgl = new Date(tanggal);
    const year = tgl.getFullYear();
    const month = String(tgl.getMonth() + 1).padStart(2, "0");
    const day = String(tgl.getDate()).padStart(2, "0");
    const date_string = `${year}-${month}-${day}`;

    // Ambil data pendonor untuk ditampilkan di modal konfirmasi
    await ambilPendonorDanTampilkanModal(
      golda,
      nama,
      jumlah,
      date_string,
      lokasi,
      permintaanID
    );
  };

  // Fungsi untuk trigger pengiriman dari modal
  const handleKirimPesanWhatsApp = async () => {
    if (!permintaanID || !listPendonor || listPendonor.length === 0) {
      swal("Info", "Data pendonor tidak ditemukan", "error");
      return;
    }

    // Mulai kirim pesan
    await kirimWhatsAppLangsung(listPendonor, permintaanID);
  };

  const getListAlasan = async () => {
    var data1 = [];

    try {
      const response = await axios.get(API_DONOR_DARAH + "tujuandonor");
      data1 = response.data.data;

      if (data1.length > 0) {
        setListAlasan(data1);
      } else {
        setListAlasan([]);
      }
      // berita.gambarKu = berita.gambar.split(',');
      // berita.isiKu = berita.isi.split('##');
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <div className="content">
        <Row>
          <Col md="8">
            <Card>
              <CardHeader>
                <h5 className="title">Formulir Permintaan Darah</h5>
              </CardHeader>
              <CardBody>
                <Form>
                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <label>Jenis Permintaan</label>
                        <Input
                          type="select"
                          value={jenisPermintaan}
                          onChange={(e) => {
                            setJenisPermintaan(e.target.value);
                            if (e.target.value === "bank_darah") {
                              setNama("Bank Darah");
                            } else {
                              setNama("");
                            }
                          }}
                          required
                        >
                          <option value="">-pilih jenis permintaan-</option>
                          <option value="bank_darah">Bank Darah</option>
                          <option value="pasien">Pasien</option>
                        </Input>
                      </FormGroup>
                    </Col>

                    {jenisPermintaan === "pasien" && (
                      <Col md="12">
                        <FormGroup>
                          <label>Nama Pasien</label>
                          <Input
                            placeholder="Nama pasien"
                            type="text"
                            value={nama}
                            onChange={(e) => setNama(e.target.value)}
                            required
                          />
                        </FormGroup>
                      </Col>
                    )}

                    <Col md="12">
                      <FormGroup>
                        <label>Tanggal Permintaan Darah</label>
                        <Input
                          defaultValue="Creative Code Inc."
                          placeholder="Company"
                          type="date"
                          value={tanggal}
                          onChange={(e) => setTanggal(e.target.value)}
                          required
                        />
                      </FormGroup>
                    </Col>

                    <Col md="12">
                      <FormGroup>
                        <label>Jenis Pendonor</label>
                        <Input
                          defaultValue="Creative Code Inc."
                          placeholder=""
                          type="select"
                          value={alasan}
                          onChange={(e) => setAlasan(e.target.value)}
                          required
                        >
                          <option value={""}>-pilih jenis pendonor-</option>
                          <option value="1">
                            Pendonor Pengganti (untuk permintaan pasien)
                          </option>
                          <option value="2">
                            Pendonor Sukarela (untuk permintaan bank darah)
                          </option>
                        </Input>
                      </FormGroup>
                    </Col>

                    <Col md="12">
                      <FormGroup>
                        <label>Jumlah Kantung</label>
                        <Input
                          defaultValue="Creative Code Inc."
                          placeholder="jumlah"
                          type="number"
                          value={jumlah}
                          onChange={(e) => setJumlah(e.target.value)}
                          required
                        />
                      </FormGroup>
                    </Col>

                    <Col md="12">
                      <FormGroup>
                        <label>Golongan Darah</label>
                        <Input
                          defaultValue="Creative Code Inc."
                          placeholder="Company"
                          type="select"
                          value={golda}
                          onChange={(e) => setGolda(e.target.value)}
                          required
                        >
                          <option value={""}>-pilih golda-</option>
                          {listGolda.map((item) => (
                            <option value={item.bloodTypeID}>
                              {item.golonganDarah}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>

                    <Col md="12">
                      <FormGroup>
                        <label>Lokasi Donor</label>
                        <Input
                          defaultValue="Creative Code Inc."
                          placeholder="Company"
                          type="select"
                          value={lokasi}
                          onChange={(e) => setLokasi(e.target.value)}
                          required
                        >
                          <option value={""}>-pilih lokasi donor-</option>
                          {listLokasi.map((item) => (
                            <option value={item.id}>{item.lokasi}</option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
              <CardFooter>
                <Button
                  className="btn-fill"
                  color="primary"
                  onClick={handleKirim}
                >
                  Simpan Permintaan
                </Button>
              </CardFooter>
            </Card>
          </Col>
          <Col md="4">
            <Card>
              <CardHeader>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <i
                    className="fab fa-whatsapp"
                    style={{ fontSize: "24px", color: "#25D366" }}
                  />
                  <h5 className="title" style={{ margin: 0 }}>
                    Generator Pesan WhatsApp
                  </h5>
                </div>
              </CardHeader>
              <CardBody>
                <FormGroup>
                  <Label>
                    <strong>Draft Pesan (Dapat Diedit):</strong>
                  </Label>
                  <Input
                    type="textarea"
                    rows={20}
                    value={draftPesan}
                    onChange={(e) => {
                      setDraftPesan(e.target.value);
                      // Tandai bahwa user sudah edit manual
                      isManualEditRef.current = true;
                    }}
                    style={{
                      fontSize: "0.9rem",
                      fontFamily: "monospace",
                      resize: "vertical",
                    }}
                    placeholder="Pesan akan otomatis ter-generate saat form diisi..."
                  />
                </FormGroup>
                <div
                  style={{
                    marginTop: "15px",
                    padding: "10px",
                    background: "#f8f9fa",
                    borderRadius: "5px",
                  }}
                >
                  <small style={{ color: "#666" }}>
                    <i className="fas fa-info-circle" /> Pesan akan otomatis
                    ter-generate berdasarkan data form yang diisi. Anda dapat
                    mengedit pesan sebelum mengirim.
                  </small>
                </div>
              </CardBody>
              <CardFooter>
                <Button
                  className="btn-fill"
                  color="success"
                  onClick={handleKirimPesan}
                  disabled={!canSendMessage || !draftPesan}
                  style={{ width: "100%" }}
                >
                  <i
                    className="fab fa-whatsapp"
                    style={{ marginRight: "8px" }}
                  />
                  Kirim Pesan
                </Button>
              </CardFooter>
            </Card>
          </Col>
        </Row>

        {/* Modal Konfirmasi WhatsApp */}
        <Modal
          isOpen={modalWhatsAppOpen}
          toggle={() => {
            if (!sendingWhatsApp) {
              setModalWhatsAppOpen(false);
              setDetailPengiriman([]); // Kosongkan tabel progress saat modal ditutup
            }
          }}
          size="lg"
        >
          <ModalHeader
            toggle={() => {
              if (!sendingWhatsApp) {
                setModalWhatsAppOpen(false);
                setDetailPengiriman([]);
              }
            }}
          >
            <h5
              style={{
                margin: 0,
                fontSize: "1.1rem",
                fontWeight: "500",
              }}
            >
              Konfirmasi Pengiriman Pesan WhatsApp
            </h5>
          </ModalHeader>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "15px 24px",
              borderBottom: "1px solid #e9ecef",
              backgroundColor: "#f8f9fa",
            }}
          >
            <div>
              {sendingWhatsApp ? (
                <Button color="danger" size="sm" onClick={handleCancelSending}>
                  Batalkan Pengiriman
                </Button>
              ) : (
                <Button
                  color="secondary"
                  size="sm"
                  onClick={() => {
                    setModalWhatsAppOpen(false);
                    setDetailPengiriman([]);
                    resetForm();
                  }}
                >
                  Batal
                </Button>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {sendingWhatsApp && (
                <span style={{ color: "#666", marginRight: "10px" }}>
                  Mengirim... ({progressWhatsApp.current}/
                  {progressWhatsApp.total})
                </span>
              )}
              {!sendingWhatsApp && (
                <Button
                  color="success"
                  size="sm"
                  onClick={() => {
                    console.log("Tombol Kirim Pesan WhatsApp diklik");
                    kirimWhatsApp();
                  }}
                  disabled={listPendonor.length === 0}
                >
                  <i
                    className="fab fa-whatsapp"
                    style={{ marginRight: "5px" }}
                  />
                  Kirim Pesan WhatsApp
                </Button>
              )}
            </div>
          </div>
          <ModalBody>
            <div>
              {!sendingWhatsApp && (
                <>
                  <h5>Detail Permintaan:</h5>
                  <Table bordered size="sm">
                    <tbody>
                      <tr>
                        <td>
                          <strong>Golongan Darah:</strong>
                        </td>
                        <td>{selectedGoldaName}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Jumlah Kantung:</strong>
                        </td>
                        <td>{jumlahKantung} kantong</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Untuk:</strong>
                        </td>
                        <td>{namaPenerima}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Tanggal:</strong>
                        </td>
                        <td>{tanggalPermintaan}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Lokasi:</strong>
                        </td>
                        <td>{lokasiDonor}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Target Pendonor:</strong>
                        </td>
                        <td>
                          <Badge color="primary">
                            {listPendonor.length} pendonor
                          </Badge>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </>
              )}

              {sendingWhatsApp && (
                <div style={{ marginTop: 20, marginBottom: 20 }}>
                  <h5>Progress Pengiriman:</h5>
                  {batchProgress.total > 1 && (
                    <div style={{ marginBottom: 10 }}>
                      <strong>
                        Batch: {batchProgress.current} / {batchProgress.total}
                      </strong>
                    </div>
                  )}
                  <Progress
                    value={
                      (progressWhatsApp.current / progressWhatsApp.total) * 100
                    }
                    color="success"
                    style={{ height: "25px" }}
                  >
                    {progressWhatsApp.current} / {progressWhatsApp.total} pesan
                    terkirim
                  </Progress>
                </div>
              )}

              {/* Tampilkan progress dan tabel hanya setelah pengiriman dimulai */}
              {(sendingWhatsApp || detailPengiriman.length > 0) && (
                <>
                  <h5 style={{ marginTop: 20 }}>Progress Pengiriman Pesan</h5>
                  <div
                    style={{
                      maxHeight: "400px",
                      overflowY: "auto",
                      border: "1px solid #dee2e6",
                      borderRadius: "5px",
                      padding: "10px",
                    }}
                  >
                    {detailPengiriman.length > 0 ? (
                      <Table size="sm" bordered>
                        <thead>
                          <tr>
                            <th>No</th>
                            <th>Nama</th>
                            <th>Tujuan</th>
                            <th>No. HP</th>
                            <th>Pesan</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailPengiriman.map((item, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>{item.nama}</td>
                              <td>{item.tujuan}</td>
                              <td>{item.noHp}</td>
                              <td>
                                <div
                                  style={{
                                    maxWidth: "300px",
                                    maxHeight: "100px",
                                    overflow: "auto",
                                    fontSize: "0.8rem",
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {item.pesan}
                                </div>
                              </td>
                              <td>
                                {item.status === "Berhasil" ? (
                                  <Badge color="success">{item.status}</Badge>
                                ) : item.status === "Gagal" ||
                                  item.status === "Error" ? (
                                  <Badge color="danger">{item.status}</Badge>
                                ) : (
                                  <Badge color="warning">{item.status}</Badge>
                                )}
                                {item.response && (
                                  <div
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#666",
                                      marginTop: "5px",
                                    }}
                                  >
                                    {item.response}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          </ModalBody>
        </Modal>
      </div>
    </>
  );
}

export default PermintaanDonor;
